import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useStreamingResponseMutation } from '../../../../hooks/useStreamingResponseMutation';
import {
	createContentStateWithHtmlStringValue,
	getSocialMediaPlainTextContentFromRawRichContent,
	tinymceEditorPlaceholderStyles,
} from '../../../../models/UiUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { useUpdateSuggestionTemplateMutation } from '../../../../queries/ContentCalendarSuggestion/useUpdateSuggestionTemplateMutation';
import { useEmailPreviewQuery } from '../../../../queries/Email/useEmailPreviewQuery';
import { useTemplate } from '../../../../queries/Templates/useTemplate';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { RichContentDocumentEditor } from '../../../components/richContent/RichContentDocumentEditor';
import { PreviewSocialMediaPost } from '../../../components/socialMedia/PreviewSocialMediaPost';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { TextArea } from '../../../components-v2/TextArea';
import { ThinkingIndicator } from '../../../components-v2/ai/ThinkingIndicator';
import { styleSheet } from './styles';

type Draft = { content: Api.IRawRichTextContentState; subject?: string };

type Props = {
	entry: Api.ContentCalendarEntryToolCall;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	impersonationContext: Api.IImpersonationContext;
};

type StreamParams = { assistantName: string; convId: string; body: object };

const DEFAULT_SOCIAL_PREVIEW_TARGETS: Api.IPostTarget[] = [
	{ provider: Api.SocialMediaType.Facebook },
	{ provider: Api.SocialMediaType.Instagram },
	{ provider: Api.SocialMediaType.LinkedIn },
];

function toGenerationContentType(contentType: string): 'email' | 'social' | 'blogTemplate' | null {
	if (contentType === 'Email') return 'email';
	if (contentType === 'SocialMediaPost' || contentType === 'Social') return 'social';
	if (contentType === 'Blog') return 'blogTemplate';
	return null;
}

export function AiGeneratedContentRefineDialog({ entry, open, onOpenChange, impersonationContext }: Props) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const queryClient = useQueryClient();

	const [draft, setDraft] = useState<Draft | null>(null);
	const [conversationId, setConversationId] = useState<string | null>(null);
	const [instruction, setInstruction] = useState('');
	const [generationError, setGenerationError] = useState<string | null>(null);
	const [conflictError, setConflictError] = useState(false);

	useEffect(() => {
		if (!open) {
			setDraft(null);
			setConversationId(null);
			setInstruction('');
			setGenerationError(null);
			setConflictError(false);
		}
	}, [open, entry.entryId]);

	const {
		data: previewTemplate,
		isLoading: isLoadingTemplate,
		isError: isTemplateError,
	} = useTemplate({
		templateId: entry.templateId ?? '',
		enabled: open && !!entry.templateId,
		impersonationContext,
	});

	const contentForPreview = draft?.content ?? (previewTemplate?.content as Api.IRawRichTextContentState | undefined);

	const emailPreviewQuery = useEmailPreviewQuery({
		content: contentForPreview as Api.IRawRichTextContentState,
		enabled: open && entry.contentType === 'Email' && !!contentForPreview,
	});

	const emailSrcDoc = useMemo(() => {
		if (!emailPreviewQuery.data) return '';
		return `<html><head><link href="https://fonts.googleapis.com/css?family=Open+Sans" rel="stylesheet"/><style>${tinymceEditorPlaceholderStyles}</style></head><body style="padding:12px;margin:0;font-family:'Open Sans',sans-serif;">${emailPreviewQuery.data}</body></html>`;
	}, [emailPreviewQuery.data]);

	const blogContentState = useMemo(() => {
		if (entry.contentType !== 'Blog') return null;
		const raw = draft?.content ?? (previewTemplate as Api.IBlogTemplate | undefined)?.content;
		if (!raw?.document) return null;
		const stripped = raw.document.replace(
			new RegExp(`</?${Api.RawRichTextContentDocumentRootElementName}>`, 'gim'),
			''
		);
		return createContentStateWithHtmlStringValue(stripped);
	}, [entry.contentType, draft, previewTemplate]);

	const { mutateAsync: createConversation, isPending: isCreatingConversation } =
		useAIAssistantCreateConversationMutation({
			onError: error => errorMessages.pushApiError(error),
		});

	// Ref-based accumulator to avoid stale closures between onEvent and onSuccess
	const latestToolCallRef = useRef<Api.EmailToolCall | Api.SocialMediaToolCall | Api.BlogToolCall | null>(null);

	const streamingMutation = useStreamingResponseMutation<StreamParams>({
		mutationFn: ({ assistantName, convId, body }) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${convId}/stream`,
						impersonationContext,
					}),
					'POST',
					body,
					resolve,
					reject
				);
			}),
		onEvent: event => {
			if (event.event === 'Error') {
				setGenerationError('Failed to generate content. Please try again.');
				return;
			}
			if (event.event !== 'ToolResponse') return;
			const data = JSON.parse(event.data ?? '{}');
			const toolCall = data?.toolCall;
			if (toolCall?.body || toolCall?.content) {
				latestToolCallRef.current = toolCall;
			}
		},
		onSuccess: () => {
			const toolCall = latestToolCallRef.current;
			latestToolCallRef.current = null;
			if (!toolCall) {
				setGenerationError('Failed to generate content. Please try again.');
				return;
			}
			if (entry.contentType === 'Email') {
				const c = toolCall as Api.EmailToolCall;
				if (c.body) setDraft({ content: c.body, subject: c.subject });
				else setGenerationError('Failed to generate content. Please try again.');
			} else if (entry.contentType === 'SocialMediaPost' || entry.contentType === 'Social') {
				const c = toolCall as Api.SocialMediaToolCall;
				if (c.content) setDraft({ content: c.content });
				else setGenerationError('Failed to generate content. Please try again.');
			} else if (entry.contentType === 'Blog') {
				const c = toolCall as Api.BlogToolCall;
				if (c.content) setDraft({ content: c.content, subject: c.title });
				else setGenerationError('Failed to generate content. Please try again.');
			}
		},
		onError: () => {
			latestToolCallRef.current = null;
			setGenerationError('Failed to generate content. Please try again.');
		},
	});

	const updateMutation = useUpdateSuggestionTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['template'] });
			onOpenChange(false);
		},
		onError: error => {
			// 409 Conflict means the suggestion was modified elsewhere since we last loaded it
			if ((error as any)?.systemCode === 409) {
				setConflictError(true);
			} else {
				errorMessages.pushApiError(error as Api.IOperationResultNoValue);
			}
		},
	});

	const isGenerating = isCreatingConversation || streamingMutation.isLoading || streamingMutation.isStreaming;
	const canRefine = !isGenerating && !!instruction.trim() && !isLoadingTemplate;

	const handleRefine = async () => {
		if (!canRefine) return;
		setGenerationError(null);
		setConflictError(false);

		const generationType = toGenerationContentType(entry.contentType);
		if (!generationType) return;

		let convId = conversationId;
		if (!convId) {
			try {
				const conv = await createConversation({ assistantName: 'ContentGenerationAgent', impersonationContext });
				setConversationId(conv.id);
				convId = conv.id;
			} catch {
				return;
			}
		}

		const action: Record<string, unknown> = {
			_type: 'ContentGenerationAction',
			contentType: generationType,
			prompt: instruction,
		};
		if (draft) {
			action.content = draft.content;
		} else {
			action.contentId = entry.templateId;
		}

		latestToolCallRef.current = null;
		streamingMutation.mutate({
			assistantName: 'ContentGenerationAgent',
			convId,
			body: { _type: 'AIActionRequest', action },
		});
	};

	const handleAccept = () => {
		if (!draft || !entry.templateId) return;
		updateMutation.mutate({
			entryId: entry.entryId,
			subject: draft.subject,
			content: draft.content,
			templateId: entry.templateId,
			impersonationContext,
		});
	};

	const isLoadingPreview = isLoadingTemplate || emailPreviewQuery.isLoading;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogHeader>
					<DialogTitle>
						{entry.templateName} — {entry.scheduledDate}
					</DialogTitle>
				</DialogHeader>
				<DialogBody styles={[styleSheet.body]}>
					<div className={css(styleSheet.previewPanel)}>
						{isLoadingPreview ? (
							<LoadingSpinner />
						) : isTemplateError ? (
							<div className={css(styleSheet.previewUnavailable)}>Preview unavailable</div>
						) : entry.contentType === 'Email' ? (
							<iframe title='Email preview' className={css(styleSheet.previewFrame)} srcDoc={emailSrcDoc} sandbox='' />
						) : entry.contentType === 'SocialMediaPost' || entry.contentType === 'Social' ? (
							<div className={css(styleSheet.socialScroll)}>
								<PreviewSocialMediaPost
									postTargets={DEFAULT_SOCIAL_PREVIEW_TARGETS}
									postContent={getSocialMediaPlainTextContentFromRawRichContent(
										contentForPreview as Api.IRawRichTextContentState
									)}
									postImages={(previewTemplate as Api.ITemplate)?.attachments}
									impersonationCtx={impersonationContext}
									templateId={entry.templateId}
								/>
							</div>
						) : entry.contentType === 'Blog' && blogContentState ? (
							<div className={css(styleSheet.blogScroll)}>
								<RichContentDocumentEditor contentState={blogContentState} readOnly />
							</div>
						) : null}
						{isGenerating && (
							<div className={css(styleSheet.thinkingOverlay)}>
								<ThinkingIndicator showTime={false} />
							</div>
						)}
					</div>
					<div className={css(styleSheet.refineSection)}>
						<span className={css(styleSheet.refineLabel)}>How would you like to customize this?</span>
						<div className={css(styleSheet.refineRow)}>
							<TextArea
								styles={[styleSheet.refineTextArea]}
								value={instruction}
								onChange={e => setInstruction(e.target.value)}
								placeholder='Add a call to action, change the tone, focus on a specific topic...'
								maxLength={500}
								disabled={isGenerating}
								rows={3}
							/>
							<Button onClick={handleRefine} disabled={!canRefine} isLoading={isGenerating}>
								Refine with AI ✦
							</Button>
						</div>
						{generationError && <p className={css(styleSheet.errorText)}>{generationError}</p>}
						{conflictError && (
							<p className={css(styleSheet.errorText)}>
								This entry was modified elsewhere. Please close and reopen to see the latest version.
							</p>
						)}
					</div>
				</DialogBody>
				{draft && (
					<DialogFooter styles={[styleSheet.footer]}>
						<Button variant='secondary' onClick={() => onOpenChange(false)} disabled={updateMutation.isPending}>
							Discard changes
						</Button>
						<Button onClick={handleAccept} isLoading={updateMutation.isPending} disabled={updateMutation.isPending}>
							Accept this version
						</Button>
					</DialogFooter>
				)}
			</DialogPopup>
		</Dialog>
	);
}
